import React from "react";
import { useTranslation } from "react-i18next";
import { cn } from "../../../lib/utils";
import { PRIMARY_CATEGORIES } from "../constants";
import type { WizardData } from "../hooks/useWizardState";

const DISABLED_CATEGORIES = new Set(["furniture", "outdoor", "security"]);

interface Props {
  data: WizardData;
  updateData: (patch: Partial<WizardData>) => void;
  onNext: () => void;
}

export function CategorySelection({ data, updateData, onNext }: Props) {
  const { t } = useTranslation("addAsset");

  const handleSelect = (categoryId: string) => {
    if (DISABLED_CATEGORIES.has(categoryId)) return;
    updateData({
      primaryCategory: categoryId,
      // Reset downstream selections when category changes
      subcategory: "",
      subcategoryLabel: "",
      brand: "",
    });
    // Auto-advance after a brief delay for visual feedback
    setTimeout(onNext, 200);
  };

  return (
    <div className="animate-fade-in-up">
      <div className="px-6 pb-3 pt-4">
        <h2 className="text-2xl font-light tracking-tight">
          {t("assetType.title")}
        </h2>
        <p className="text-sm text-muted-foreground mt-1">
          {t("assetType.chooseCategory")}
        </p>
      </div>

      <div className="px-6 pb-6 grid grid-cols-2 gap-4 mt-5">
        {PRIMARY_CATEGORIES.map((cat, idx) => {
          const Icon = cat.icon;
          const isSelected = data.primaryCategory === cat.id;
          const isDisabled = DISABLED_CATEGORIES.has(cat.id);

          return (
            <button
              key={cat.id}
              onClick={() => handleSelect(cat.id)}
              disabled={isDisabled}
              style={{ animationDelay: `${idx * 0.06}s` }}
              className={cn(
                "animate-fade-in-up relative flex flex-col items-center justify-center gap-3 p-6 rounded-2xl border-2 transition-all duration-200 shadow-sm",
                isDisabled
                  ? "opacity-40 cursor-not-allowed bg-muted/50 border-border"
                  : [
                      "cursor-pointer group hover:shadow-md",
                      "hover:border-primary/50 hover:bg-primary/[0.03] hover:shadow-sm",
                      isSelected
                        ? "border-primary bg-primary/5 shadow-sm"
                        : "border-border bg-background",
                    ]
              )}
            >
              {isDisabled && (
                <span className="absolute top-2 right-2 text-[9px] font-semibold uppercase tracking-wide text-muted-foreground bg-muted border border-border px-1.5 py-0.5 rounded-full leading-none">
                  {t("assetType.soon")}
                </span>
              )}
              <div
                className={cn(
                  "w-14 h-14 rounded-xl flex items-center justify-center transition-colors",
                  isSelected
                    ? "bg-primary text-primary-foreground"
                    : "bg-muted text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary"
                )}
              >
                <Icon className="h-6 w-6" />
              </div>
              <div className="text-center">
                <span className="text-sm font-medium block">
                  {t(`assetType.cats.${cat.id}.label`, { defaultValue: cat.label })}
                </span>
                <span className="text-[11px] text-muted-foreground leading-tight block mt-0.5">
                  {t(`assetType.cats.${cat.id}.desc`, { defaultValue: cat.description })}
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

